// How a part is named to people: by its drawing position («поз. 18») — the
// bare name repeats («Балка», «ПЛАСТИНА» dozens of times in one project) — and,
// when known, its section, which tells same-named parts apart
// (поз. 8 · Пластини · BL8x120 vs поз. 9 · Пластини · BL6x41).

export function partPosition(no: number | null | undefined): string | null {
  return no == null ? null : `поз. ${no}`
}

export function partLabel(part: {
  no?: number | null
  name: string
  section?: string | null
}): string {
  return [partPosition(part.no), part.name, part.section || null]
    .filter((x): x is string => Boolean(x))
    .join(' · ')
}

/**
 * A mark as people name it: «Марка Б/1» when the name already carries the
 * code, otherwise «Б/1 · Опора» — never the bare name, which repeats.
 */
export function assemblyLabel(asm: { code: string; name: string }): string {
  if (!asm.name) return asm.code
  return asm.name.includes(asm.code) ? asm.name : `${asm.code} · ${asm.name}`
}

const collator = new Intl.Collator('uk', { numeric: true, sensitivity: 'base' })

/** Codes as people read them: «Б/2» before «Б/10», not after. */
export function naturalCompare(a: string, b: string): number {
  return collator.compare(a, b)
}

/** Parts in drawing order (поз. 1, 2, … 10), the unnumbered last by code. */
export function byPosition<T extends { no?: number | null; code: string }>(a: T, b: T): number {
  const an = a.no ?? Number.POSITIVE_INFINITY
  const bn = b.no ?? Number.POSITIVE_INFINITY
  return an !== bn ? an - bn : naturalCompare(a.code, b.code)
}

/**
 * Work-order lines of marks in natural order (Б/1, Б/2 … Б/10). Lines of
 * parts keep the order they were added in — that order is the cutter's.
 */
export function inMarkOrder<T extends { assembly_code?: string | null }>(lines: readonly T[]): T[] {
  if (lines.length === 0 || lines.some((l) => !l.assembly_code)) return [...lines]
  return [...lines].sort((a, b) => naturalCompare(a.assembly_code ?? '', b.assembly_code ?? ''))
}
